import React, { CSSProperties, FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { RouteComponentProps } from 'react-router-dom';
import './styles/index.css';
import { EposThemeProvider, useEposTheme } from './Providers/ThemeProvider';
import { Stage, StageNoBackground, StageScrim } from './styles';
import { ExpressCartProvider, useExpressCartActions } from './Providers/ExpressCartProvider';
import {
  ExpressProductLibraryProvider,
  useExpressProductLibrary,
} from './Providers/ExpressProductLibraryProvider';
import { useI18n, useLogger, useSelector } from '~Hooks';
import Welcome from './Scenes/Welcome';
import Menu, { MENU_EXIT_MS } from './Scenes/Menu';
import Checkout from './Scenes/Checkout';
import { ExpressOrderProvider, useExpressOrder } from './Providers/ExpressOrderProvider';
import { ExpressLoyaltyProvider, useExpressLoyalty } from './Providers/ExpressLoyaltyProvider';
import OrderIdMaster from './Components/OrderIdMaster';
import BootSpinner from './Components/BootSpinner';
import InitialLoader from './Components/InitialLoader';
import NoData from './Components/NoData';
import Onboarding from '~Scenes/Onboarding';
import BongMaster from '~Scenes/Tables/Components/BongMaster';
import MenuManagerBridge from './Components/MenuManagerBridge';
import ClearCartConfirm from './Components/ClearCartConfirm';
import IdleTimeout from './Components/IdleTimeout';
import PaperOutNotice from './Components/PaperOutNotice';
import useBackgroundImageUrl from './Hooks/useBackgroundImageUrl';
import { SessionActiveContext } from './Hooks/useSessionActive';
import BackgroundVideo from './Components/BackgroundVideo';
import { resolveBackgroundMedia } from './VideoThemes';

/**
 * The welcome background stays on screen for the whole flow; picking a service
 * type flies the menu sheet up over it from the bottom, leaving the top of the
 * screen showing through. Both are CSS transitions on a stable tree, so nothing
 * re-renders while they play.
 */
interface Props {
  venueId: string;
}

const EposFlow: FC<Props> = ({ venueId }) => {
  const logger = useLogger('epos');
  const { i18n } = useI18n();
  const eposTheme = useEposTheme();
  const cartActions = useExpressCartActions();
  /** Stable no-op unless the venue has Loyalty Pro and an Adyen terminal. */
  const expressLoyalty = useExpressLoyalty();
  /** The product library downloads the menu up front so the shopper never waits inside the sheet. */
  const productLibrary = useExpressProductLibrary();

  const companyId = useSelector((state) => state.company);

  /**
   * Backoffice video > bundled theme video (VideoThemes) > backoffice image > nothing.
   * See resolveBackgroundMedia.
   */
  const backgroundMedia = useMemo(
    () => resolveBackgroundMedia(eposTheme.backgroundMediaSrcUrl, companyId),
    [eposTheme.backgroundMediaSrcUrl, companyId],
  );

  const backgroundVideoUrl = backgroundMedia?.type === 'video' ? backgroundMedia.src : null;

  /** Screen-sized CDN copy of the backoffice image, or the original if the resizer fails. */
  const backgroundUrl = useBackgroundImageUrl(
    backgroundMedia?.type === 'image' ? backgroundMedia.src : null,
  );

  const themeVars = useMemo(() => {
    const vars: Record<string, string> = {};
    // Same meaning as the labels in backoffice - see styles/index.css.
    if (eposTheme.main) vars['--eposPrimary'] = eposTheme.main;
    if (eposTheme.contrastingColor) vars['--eposAccent'] = eposTheme.contrastingColor;
    if (eposTheme.secondary) vars['--eposButton'] = eposTheme.secondary;
    if (eposTheme.priceColor) vars['--eposPrice'] = eposTheme.priceColor;
    if (eposTheme.menuBackgroundColor) {
      vars['--eposCategoryBackground'] = eposTheme.menuBackgroundColor;
    }
    if (eposTheme.menuTextColor) vars['--eposCategoryText'] = eposTheme.menuTextColor;
    return vars as CSSProperties;
  }, [eposTheme]);

  /** Modals portal to body, outside Stage - give body the same theme vars so they match. */
  useEffect(() => {
    const vars = Object.entries(themeVars as Record<string, string>);
    vars.forEach(([name, value]) => document.body.style.setProperty(name, value));
    return () => vars.forEach(([name]) => document.body.style.removeProperty(name));
  }, [themeVars]);

  const [started, setStarted] = useState<boolean>(false);
  /** Bumped every time a fresh order starts, so Menu knows to scroll back to the top. */
  const [sessionKey, setSessionKey] = useState(0);
  /** True from "Continue to payment" until the order is done or abandoned. */
  const [checkingOut, setCheckingOut] = useState<boolean>(false);
  /** True while "Start over" is waiting on confirmation to empty a non-empty cart. */
  const [confirmingStartOver, setConfirmingStartOver] = useState<boolean>(false);
  /** Bumped after every completed purchase - PaperOutNotice checks the printer on each bump. */
  const [purchaseCount, setPurchaseCount] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const performStartOver = useCallback(() => {
    logger.info('Shopper restarted the EPOS flow', { venueId });
    // A card tapped for loyalty but never paid with must be handed back to the terminal.
    expressLoyalty.release();
    setCheckingOut(false);
    setStarted(false);
  }, [venueId, expressLoyalty]);

  /** A non-empty cart must be confirmed away before starting over - an empty one goes straight back. */
  const handleStartOver = useCallback(() => {
    if (cartActions.hasItems()) {
      logger.info('Start over requested with items still in the cart - asking to confirm', {
        venueId,
      });
      setConfirmingStartOver(true);
      return;
    }
    performStartOver();
  }, [cartActions, performStartOver, venueId]);

  const handleConfirmStartOver = useCallback(() => {
    logger.info('Confirmed clearing the cart to start over', { venueId });
    cartActions.clear();
    setConfirmingStartOver(false);
    performStartOver();
  }, [cartActions, performStartOver, venueId]);

  const handleCancelStartOver = useCallback(() => setConfirmingStartOver(false), []);

  /** Nobody touched the screen for too long - drop everything and greet the next shopper. */
  const handleIdleTimeout = useCallback(() => {
    logger.info('Idle timeout - clearing the cart and returning to welcome', { venueId });
    cartActions.clear();
    setConfirmingStartOver(false);
    performStartOver();
  }, [cartActions, performStartOver, venueId]);

  const handleCheckout = useCallback(() => {
    logger.info('Entering checkout', { venueId });
    setCheckingOut(true);
  }, [venueId]);

  /** Backed out of the checkout before paying - the sheet flies back up. */
  const handleCheckoutBack = useCallback(() => {
    logger.info('Left checkout without paying', { venueId });
    setCheckingOut(false);
  }, [venueId]);

  /** Paid and shown the number - a fresh start for the next shopper. */
  const handleCheckoutDone = useCallback(() => {
    logger.info('Order complete, resetting to welcome', { venueId });
    setCheckingOut(false);
    setStarted(false);
    setPurchaseCount((count) => count + 1);
  }, [venueId]);

  /** Welcome has already set the service type on the cart by the time this fires. */
  const handleStart = useCallback(() => {
    logger.info('Starting EPOS order', { venueId });
    setStarted(true);
    setSessionKey((key) => key + 1);
  }, [venueId]);

  // A short unthemed spinner first - once the theme lands the real loader can
  // come up in the venue's own colors instead of flashing the defaults.
  /**
   * Last child of every branch below, so it stays mounted from the boot spinner
   * on - the menu is already assigned when the stage first renders.
   */
  const menuManager = <MenuManagerBridge sessionActive={started} />;

  if (eposTheme.loading) {
    return (
      <>
        <BootSpinner />
        {menuManager}
      </>
    );
  }

  if (productLibrary.loading) {
    return (
      <>
        <InitialLoader
          venueId={venueId}
          themeVars={themeVars}
          progress={productLibrary.progress}
          progressText={productLibrary.progressText}
        />
        {menuManager}
      </>
    );
  }

  if (!productLibrary.hasData) {
    logger.error('No EPOS menu data available', { venueId });
    return (
      <>
        <NoData venueId={venueId} themeVars={themeVars} />
        {menuManager}
      </>
    );
  }

  return (
    <>
      {/* Back on welcome closes the promo code, comment and loyalty modals - see useSessionActive. */}
      <SessionActiveContext.Provider value={started}>
        <Stage style={themeVars} backgroundUrl={backgroundUrl}>
          {/* Paused while a shopper is ordering - nobody watches it behind the sheet. */}
          {backgroundVideoUrl && <BackgroundVideo src={backgroundVideoUrl} paused={started} />}
          {!backgroundUrl && !backgroundVideoUrl && (
            <StageNoBackground>{i18n('SSK.NoBackground')}</StageNoBackground>
          )}

          <StageScrim />

          <Welcome onStart={handleStart} visible={!started} />

          {/* Mounted up front, off-screen under the welcome background, so the
          first "Eat here"/"Take away" tap only plays the slide transition
          instead of also having to mount and render the whole menu sheet. */}
          <CSSTransition
            in={started && !checkingOut}
            nodeRef={menuRef}
            timeout={MENU_EXIT_MS}
            classNames="epos-menu">
            <Menu
              ref={menuRef}
              sessionKey={sessionKey}
              onStartOver={handleStartOver}
              onCheckout={handleCheckout}
            />
          </CSSTransition>

          {checkingOut && <Checkout onBack={handleCheckoutBack} onDone={handleCheckoutDone} />}

          <ClearCartConfirm
            open={confirmingStartOver}
            onConfirm={handleConfirmStartOver}
            onCancel={handleCancelStartOver}
          />

          {/* Reads Redux and the order itself, so EposFlow only re-renders when the time runs out. */}
          <IdleTimeout active={started} onTimeout={handleIdleTimeout} />

          {/* Asks the primary printer for paper after each purchase; blocks the screen until refilled. */}
          <PaperOutNotice checkKey={purchaseCount} />
        </Stage>
      </SessionActiveContext.Provider>
      {menuManager}
    </>
  );
};

/** Prints the order-number ticket for orders without a receipt - the kiosk's own component. */
const OrderPrintMaster: FC = () => {
  const { i18n } = useI18n();
  const expressOrder = useExpressOrder();
  const eposTheme = useEposTheme();
  const printMessage = eposTheme.orderNumberPrintMessage
    ? i18n(eposTheme.orderNumberPrintMessage)
    : undefined;

  return (
    <OrderIdMaster
      orderId={expressOrder.orderId}
      extraMessage={printMessage}
      qrCodeLink={eposTheme.qrCodeLink}
    />
  );
};

const EPOS: FC<RouteComponentProps<{ id: string }>> = ({ match }) => (
  <ExpressCartProvider useSeparateTakeAwayMenu>
    <ExpressProductLibraryProvider venueId={match.params.id}>
      <EposThemeProvider>
        <ExpressLoyaltyProvider>
          <ExpressOrderProvider venueId={match.params.id}>
            <EposFlow venueId={match.params.id} />
            <OrderPrintMaster />
          </ExpressOrderProvider>
        </ExpressLoyaltyProvider>

        <BongMaster />
        <Onboarding />
      </EposThemeProvider>
    </ExpressProductLibraryProvider>
  </ExpressCartProvider>
);

export default memo(EPOS);
